import { makeStyles } from '@material-ui/core/styles';
import { useModalContext } from 'contexts/ModalContext';
import React, { FC, useState } from 'react';
import { Product } from 'types';
import { getSearchSuggestions, TypesenseProduct } from '../api';
import SelectedSubstitute from './SelectedSubstitute';

const placeholderImage = 'https://via.placeholder.com/50';

interface Props {
  onSubstituteSelect: (selectedProduct: Product | null) => void;
}

const useStyles = makeStyles((theme) => ({
  input: {
    width: '100%',
    padding: '16px 8px',
    boxSizing: 'border-box',
    border: '1px solid black',
    borderRadius: '8px',
    marginTop: '12px',
  },
  dropdown: {
    border: '1px solid #ccc',
    padding: 0,
    margin: 0,
    listStyleType: 'none',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  dropdownItem: {
    padding: '8px',
    cursor: 'pointer',
    borderBottom: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  productImage: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
  },
  productInfo: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selectedContainer: {
    marginTop: '16px',
    border: '1px solid black',
    borderRadius: '8px',
    padding: '8px 6px',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    width: '98%',
    height: 'auto',
  },
  noProductsMessage: {
    textAlign: 'center',
    padding: '8px',
  },
}));

const SubstituteSearchInput: FC<Props> = ({ onSubstituteSelect }) => {
  const classes = useStyles();
  const { selectedProduct } = useModalContext();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<TypesenseProduct[]>([]);
  const [selectedSubstitute, setSelectedSubstitute] = useState<Product | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const selectedVendor = selectedProduct?.vendor || '';

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value;
    setQuery(searchQuery);
    setShowDropdown(true);

    if (searchQuery.length > 3) {
      try {
        const result = await getSearchSuggestions(searchQuery.trim(), {
          vendor: selectedVendor,
        });
        setProducts(result);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      }
    }
  };

  const handleFocus = () => {
    setShowDropdown(true);
  };

  const handleItemClick = (product: Product) => {
    setQuery(product.name);
    setSelectedSubstitute(product);
    setShowDropdown(false);
    onSubstituteSelect(product);
  };

  return (
    <div>
      {selectedSubstitute ? (
        <div
          className={classes.selectedContainer}
          onClick={() => {
            setSelectedSubstitute(null);
            setQuery('');
          }}
        >
          <SelectedSubstitute product={selectedSubstitute} />
        </div>
      ) : (
        <input
          type="text"
          value={query}
          onChange={handleSearch}
          onFocus={handleFocus}
          placeholder="Search for substitute products..."
          className={classes.input}
        />
      )}

      {showDropdown && products.length > 0 ? (
        <ul className={classes.dropdown}>
          {products.map((product) => (
            <li key={product.id} onClick={() => handleItemClick(product)} className={classes.dropdownItem}>
              <img src={product.imageURL || placeholderImage} alt={product.name} className={classes.productImage} />
              <div className={classes.productInfo}>
                <span>{product.name}</span>
                <strong>
                  R
                  {product.pricing && product.pricing[0]?.originalPrice
                    ? product.pricing[0].originalPrice.toFixed(2)
                    : 'N/A'}
                </strong>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default SubstituteSearchInput;
